import * as React from "react"
import { X } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

type TagInputProps = Omit<React.ComponentProps<"input">, "value" | "onChange"> & {
  value: string[]
  onChange: (tags: string[]) => void
  // Existing tags offered while typing; already chosen ones are left out.
  suggestions?: string[]
}

function normalizeTag(raw: string) {
  return raw.trim().replace(/\s+/g, " ")
}

// Enter or comma confirms a tag, Backspace on an empty field removes the last one.
// Arrow keys pick a suggestion, Escape closes the list. Duplicates are ignored
// case-insensitively.
const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  ({ value, onChange, suggestions = [], className, placeholder, disabled, onBlur, ...props }, ref) => {
    const [draft, setDraft] = React.useState("")
    const [activeIndex, setActiveIndex] = React.useState(-1)
    const [suggestionsOpen, setSuggestionsOpen] = React.useState(false)
    const listId = React.useId()

    const query = normalizeTag(draft).toLocaleLowerCase("de")
    const chosen = new Set(value.map((tag) => tag.toLocaleLowerCase("de")))
    const matches = query
      ? suggestions.filter((tag) => {
          const key = tag.toLocaleLowerCase("de")
          return !chosen.has(key) && key.includes(query)
        })
      : []
    const showSuggestions = suggestionsOpen && matches.length > 0

    function addTags(rawTags: string[]) {
      const next = [...value]
      const known = new Set(value.map((tag) => tag.toLocaleLowerCase("de")))
      for (const raw of rawTags) {
        const tag = normalizeTag(raw)
        const key = tag.toLocaleLowerCase("de")
        if (tag && !known.has(key)) {
          known.add(key)
          next.push(tag)
        }
      }
      if (next.length !== value.length) onChange(next)
      setDraft("")
      setActiveIndex(-1)
    }

    function removeTag(tag: string) {
      onChange(value.filter((existing) => existing !== tag))
    }

    function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
      if (showSuggestions && event.key === "ArrowDown") {
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, matches.length - 1))
      } else if (showSuggestions && event.key === "ArrowUp") {
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, -1))
      } else if (showSuggestions && event.key === "Escape") {
        // Only close the suggestion list, not a surrounding dialog.
        event.preventDefault()
        event.stopPropagation()
        setSuggestionsOpen(false)
      } else if (event.key === "Enter" || event.key === ",") {
        event.preventDefault()
        const picked = showSuggestions ? matches[activeIndex] : undefined
        addTags([picked ?? draft])
      } else if (event.key === "Backspace" && draft === "" && value.length > 0) {
        event.preventDefault()
        removeTag(value[value.length - 1])
      }
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
      const next = event.target.value
      // Pasted text like "Kraft, Ausdauer" is split into separate tags.
      if (next.includes(",")) {
        const parts = next.split(",")
        addTags(parts.slice(0, -1))
        setDraft(parts[parts.length - 1])
      } else {
        setDraft(next)
        setActiveIndex(-1)
      }
      setSuggestionsOpen(true)
    }

    return (
      <div className="relative">
        <div
          className={cn(
            "flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1.5 shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring has-[[aria-invalid=true]]:border-destructive",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
        >
          {value.map((tag) => (
            <Badge key={tag} variant="outline" className="gap-1 border-primary/20 bg-primary/10 pr-1 font-medium text-primary">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                disabled={disabled}
                aria-label={`Tag ${tag} entfernen`}
                className="rounded-sm p-0.5 text-primary/70 hover:bg-primary/15 hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <X aria-hidden="true" className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <input
            ref={ref}
            value={draft}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={(event) => {
              addTags([draft])
              setSuggestionsOpen(false)
              onBlur?.(event)
            }}
            disabled={disabled}
            placeholder={value.length === 0 ? placeholder : undefined}
            // The suggestion list replaces the browser's own autocomplete.
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
            aria-controls={showSuggestions ? listId : undefined}
            aria-activedescendant={
              showSuggestions && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
            }
            className="h-6 min-w-24 flex-1 bg-transparent px-1 text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed md:text-sm"
            {...props}
          />
        </div>
        {showSuggestions && (
          <ul
            id={listId}
            role="listbox"
            className="absolute inset-x-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          >
            {matches.map((tag, index) => (
              <li
                key={tag}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                // Keep focus in the input so picking a suggestion is not treated as a blur.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => addTags([tag])}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  "cursor-pointer rounded-sm px-2 py-1.5 text-sm",
                  index === activeIndex && "bg-primary/10 text-primary"
                )}
              >
                {tag}
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }
)
TagInput.displayName = "TagInput"

export { TagInput }
